import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  CrmContactDetail,
  CrmContactSummary,
  fetchContactDetail,
  fetchContacts,
} from "../api/contacts";
import ContactDetailDrawer from "../components/contacts/ContactDetailDrawer";

type ContactFilter = "all" | "mismatch" | "unresolved";

function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function identityLabel(contact: CrmContactSummary): string {
  const primary = contact.identity_summary[0];
  if (!primary) return "-";
  return primary.label || primary.provider;
}

export default function ContactsPage() {
  const [contacts, setContacts] = useState<CrmContactSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ContactFilter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CrmContactDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchContacts();
      setContacts(data.contacts);
    } catch (err) {
      console.error(err);
      setError("Failed to load contacts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadContacts();
  }, []);

  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((contact) => {
      if (filter === "mismatch" && contact.mismatch_count === 0) return false;
      if (filter === "unresolved" && contact.status !== "unresolved") return false;
      if (!q) return true;
      const haystack = [
        contact.display_name || "",
        ...contact.identity_summary.map((identity) => identity.label || ""),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [contacts, filter, search]);

  const openContact = async (contact: CrmContactSummary) => {
    setSelectedId(contact.crm_contact_id);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await fetchContactDetail(contact.crm_contact_id));
    } catch (err) {
      console.error(err);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={2}
        mb={3}
      >
        <Typography variant="h5">Customers</Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={loadContacts} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} gap={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search names, phones, emails"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <ToggleButtonGroup
              size="small"
              value={filter}
              exclusive
              onChange={(_event, value: ContactFilter | null) => {
                if (value) setFilter(value);
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="mismatch">Mismatches</ToggleButton>
              <ToggleButton value="unresolved">Unresolved</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : filteredContacts.length === 0 ? (
            <Typography>No customers found.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Identity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Recent activity</TableCell>
                    <TableCell align="right">Bookings</TableCell>
                    <TableCell align="right">Conversations</TableCell>
                    <TableCell align="right">Evidence</TableCell>
                    <TableCell align="right">View</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContacts.map((contact) => (
                    <TableRow
                      hover
                      key={contact.crm_contact_id}
                      onClick={() => void openContact(contact)}
                      selected={selectedId === contact.crm_contact_id}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography fontWeight={600}>
                            {contact.display_name || "Unknown"}
                          </Typography>
                          {contact.display_name_source && (
                            <Typography variant="caption" color="text.secondary">
                              {contact.display_name_source}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{identityLabel(contact)}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={contact.status} />
                      </TableCell>
                      <TableCell>{formatDateTime(contact.recent_activity_at)}</TableCell>
                      <TableCell align="right">{contact.booking_count}</TableCell>
                      <TableCell align="right">{contact.conversation_count}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end" gap={1}>
                          {contact.mismatch_count > 0 && (
                            <Tooltip title="Mismatch evidence">
                              <Chip
                                size="small"
                                color="warning"
                                icon={<WarningAmberIcon />}
                                label={contact.mismatch_count}
                              />
                            </Tooltip>
                          )}
                          {contact.possible_match_count > 0 && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Matches ${contact.possible_match_count}`}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              void openContact(contact);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ContactDetailDrawer
        open={Boolean(selectedId)}
        loading={detailLoading}
        detail={detail}
        onClose={() => {
          setSelectedId(null);
          setDetail(null);
        }}
      />
    </Box>
  );
}
